import { Component, inject } from '@angular/core';
import { BuildingItemDto, BuildingService, TeamItemDto } from '@artemis-software/wr-api';
import { mapUrls, primaryIcon } from '@pages/map-overview/map-overview.component';
import * as L from 'leaflet';
import { NbToastrService } from '@nebular/theme';
import { watch } from '@/utils/watch';
import { input } from '@angular/core';

@Component({
  standalone: false,
  selector: 'wr-team-dashboard-map',
  templateUrl: './team-dashboard-map.component.html',
  styleUrls: ['./team-dashboard-map.component.scss'],
})
export class TeamDashboardMapComponent {
  readonly team = input<TeamItemDto | undefined>();
  readonly buildingService = inject(BuildingService);
  readonly toastr = inject(NbToastrService);

  private map?: L.Map;
  private layerGroup?: L.LayerGroup;

  constructor() {
    watch(this.team, (team) => {
      this.clearMap();
      if (team?.id) {
        this.buildingService.getBuildingsByTeamId(team.id)
          .subscribe({
            next: (buildings) => this.initializeMap(buildings),
            error: () =>
              this.toastr.danger(
                'Gebäude konnten nicht geladen werden.',
                'Fehler beim Laden'
              ),
          });
      }
    });
  }

  private initializeMap(buildings: BuildingItemDto[]) {
    if (buildings.length === 0) {
      this.toastr.warning('Keine Gebäude für dieses Team gefunden.', 'Keine Daten');
      return;
    }

    const baseMapUrl = mapUrls[0];
    this.map = L.map('map');
    L.tileLayer(baseMapUrl).addTo(this.map);

    this.layerGroup = L.layerGroup().addTo(this.map);

    for (const building of buildings) {
      const coords = building.baseData.coordinates;
      if (
        coords &&
        coords.latitude !== undefined &&
        coords.longitude !== undefined &&
        !(Math.abs(coords.latitude) <= 0 && Math.abs(coords.longitude) <= 0)
      ) {
        const marker = L.marker([coords.latitude, coords.longitude], {
          icon: primaryIcon,
        }).bindPopup(`<b>${building.baseData.name}</b>`);
        marker.addTo(this.layerGroup);
      }
    }

    const bounds = L.latLngBounds(
      buildings
        .filter(b => b.baseData.coordinates && b.baseData.coordinates.latitude !== undefined && b.baseData.coordinates.longitude !== undefined)
        .map(b => [b.baseData.coordinates!.latitude, b.baseData.coordinates!.longitude] as [number, number])
    );

    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }

  }

  private clearMap() {
    if (this.layerGroup) {
      this.layerGroup.clearLayers();
      this.layerGroup.remove();
      this.layerGroup = undefined;
    }
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }
}
